import { Component, OnInit, OnDestroy, ChangeDetectorRef, ViewRef, ChangeDetectionStrategy, signal } from '@angular/core';
import { FieldType } from '@ngx-formly/core';
import { debounceTime, Subject, takeUntil } from 'rxjs';

import { AppStateService } from 'src/app/core-module/services/app-state.service';
import { Options } from '@angular-slider/ngx-slider';
import { AloAocStateService } from '../../../core-module/services/state/alo-aoc-state.service';
import { CheckboxStateService } from '../../../core-module/services/state/checkbox-state.service';
import { FeatureDefaultsStateService } from '../../../core-module/services/state/feature-defaults-state.service';
import { InputErrorsStateService } from '../../../core-module/services/state/input-errors-state.service';

@Component({
  standalone: false,
  selector: 'app-lowmainslock',
  templateUrl: './lowmainslock.html',
  styleUrls: ['./lowmainslock.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LowMainsLockComponent extends FieldType implements OnInit, OnDestroy {
  // Feature enable state
  isEnableFeature = false;
  private readonly lowmainslockDisabledSignal = signal(false);
  showEnableFeature = false;
  column: LegacyAny;

  // Low mains duration
  lowMainsDuration = 600;
  lowMainsDurationMin = 1;
  lowMainsDurationMax = 600;
  lowMainsDurationOptions: Options = {};
  lowMainsDurationDisplayValue: LegacyAny;

  // Default values from schema
  private isEnableFeatureDefault = false;
  private lowMainsDurationDefault = 600;

  // Error tracking
  inputError: LegacyAny = {};
  private readonly lowmainslockDisabledButtonSignal = signal(false);

  // Default color tracking
  private readonly defaultColorSignal = signal(true);

  // Standard/Advanced mode
  private readonly quickConfigvalueSignal = signal(false);
  quickConfigCustomCSS = 'custom-inputbox';
  setQuickConfig = 'standard';

  // Include in configuration tracking
  private readonly isIncludedInConfigurationSignal = signal(false);
  private ignoreParentUpdate = false;
  private hasConfigData = false;
  private isResetting = false;

  // Input subject for debouncing
  private readonly lowMainsDurationInputSubject = new Subject<string>();

  private properties: LegacyAny;
  private readonly destroy$ = new Subject<void>();

  get lowmainslockDisabled(): boolean { return this.lowmainslockDisabledSignal(); }
  set lowmainslockDisabled(value: boolean) { this.lowmainslockDisabledSignal.set(!!value); }

  get lowmainslockDisabledButton(): boolean { return this.lowmainslockDisabledButtonSignal(); }
  set lowmainslockDisabledButton(value: boolean) { this.lowmainslockDisabledButtonSignal.set(!!value); }

  get defaultColor(): boolean { return this.defaultColorSignal(); }
  set defaultColor(value: boolean) { this.defaultColorSignal.set(!!value); }

  get quickConfigvalue(): boolean { return this.quickConfigvalueSignal(); }
  set quickConfigvalue(value: boolean) { this.quickConfigvalueSignal.set(!!value); }

  get isIncludedInConfiguration(): boolean { return this.isIncludedInConfigurationSignal(); }
  set isIncludedInConfiguration(value: boolean) { this.isIncludedInConfigurationSignal.set(!!value); }

  constructor(
    private readonly ref: ChangeDetectorRef,
    private readonly appStateService: AppStateService,
    private readonly featureDefaults: FeatureDefaultsStateService,
    private readonly checkboxState: CheckboxStateService,
    private readonly inputErrors: InputErrorsStateService,
    private readonly aloAoc: AloAocStateService
  ) {
    super();
  }

  ngOnInit(): void {
    // Protect initial load from parent checkbox updates
    this.ignoreParentUpdate = true;

    // Get schema properties if available
    this.properties = (this.field as LegacyAny)?.properties;

    // Check if Enabled exists in required properties
    this.column = (this.field as LegacyAny).requiredProperties;
    this.showEnableFeature = this.column?.includes('Enabled') ?? false;

    // Load from schema first to initialize all values
    if (this.properties) {
      this.loadFromSchema();
    }

    // If enable feature checkbox is not shown, ensure fields are not disabled
    if (!this.showEnableFeature) {
      this.lowmainslockDisabled = false;
    }

    this.initializeSliderOptions();

    this.lowMainsDurationDisplayValue = this.lowMainsDuration;

    // Load saved configuration data
    let savedValue = null;

    // Check UnSelectedFeatConfig first (for edited but unselected features)
    const unSelectedFeat = this.appStateService.getUnSelectedFeatConfig();
    let isConfigEdited = false;

    if (unSelectedFeat != null && Array.isArray(unSelectedFeat)) {
      let featIndex = 0;
      unSelectedFeat.forEach((element) => {
        if (element['featureName'] && element['featureName'].toString().toUpperCase() === 'LOWMAINSLOCK') {
          isConfigEdited = true;
          savedValue = element['featureParameters'][0];
          unSelectedFeat.splice(featIndex, 1);
          this.appStateService.setUnSelectedFeatConfig(unSelectedFeat);
        } else {
          featIndex++;
        }
      });
    }

    // Use centralized method to get feature data (prioritizes SaveSetData over ConfigurationData)
    if (!isConfigEdited) {
      const lowmainslockData = this.readStoredFeatureData();
      if (lowmainslockData) {
        savedValue = lowmainslockData;
        // If data exists in storage, mark as hasConfigData (refresh scenario)
        this.hasConfigData = Object.keys(savedValue).length > 0;
      }
    }

    const currentValue = savedValue || this.formControl.value || {};
    if (currentValue && Object.keys(currentValue).length > 0) {
      this.loadDataFromObject(currentValue);
    }

    this.setupInputDebouncing();

    // Subscribe to restore default trigger
    this.featureDefaults.getLowMainsLockDefaultValue
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        if (response === 'valid') {
          this.defaultColor = true;
          this.resetToDefaults();
          // Reset the BehaviorSubject to prevent replaying "valid" on next subscription
          this.featureDefaults.setLowMainsLockDefaultValue('');
        }
      });

    // Subscribe to parent "Include in configuration" checkbox changes
    this.checkboxState.getLowMainsLockParentCheckbox
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        if (state !== null && !this.ignoreParentUpdate) {
          this.isIncludedInConfiguration = state.checked;

          // Handle unchecking: reset state
          if (!state.checked) {
            this.hasConfigData = false;
            this.inputErrors.setLowMainsLockInputError.next(false);
            this.ref.detectChanges();
            return;
          }

          // Handle checking: refresh flow
          if (this.hasConfigData) {
            // Refresh flow: preserve current state and send current error state
            this.sendErrorStateToParent();
            return;
          }

          this.updateFormControl();
          this.sendErrorStateToParent();
          this.ref.detectChanges();
        }
      });

    // Set initial disabled state based on enable checkbox
    this.enableFeatureChanged(this.isEnableFeature);

    // Revalidate all fields after loading from storage to restore error states
    this.revalidateAllFields();

    this.checkDefaults();

    // Enable checkbox if any value is non-default
    if (!this.defaultColor) {
      this.checkboxState.enableLowMainsLockCheckbox();
      this.isIncludedInConfiguration = true;
      this.hasConfigData = true;
    }

    // Send error state AFTER isIncludedInConfiguration is set (so parent knows about errors on refresh)
    this.sendErrorStateToParent();

    // Subscribe to Standard/Advanced mode changes
    this.aloAoc.getquickConfig
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        if (data === 'quickConfig') {
          this.quickConfigvalue = true;
          this.quickConfigCustomCSS = 'quick-custom-inputbox textBoxUnit';
          this.setQuickConfig = 'quickConfig';
        } else if (data === 'standard') {
          this.quickConfigvalue = false;
          this.quickConfigCustomCSS = 'custom-inputbox';
          this.setQuickConfig = 'standard';
        }
        setTimeout(() => {
          if (this.ref && !(this.ref as ViewRef).destroyed) {
            this.ref.detectChanges();
          }
        });
      });

    // Allow parent updates after initialization
    this.ignoreParentUpdate = false;
  }

  private initializeSliderOptions(): void {
    const emptyTicks: number[] = [];

    this.lowMainsDurationOptions = {
      floor: this.lowMainsDurationMin,
      ceil: this.lowMainsDurationMax,
      disabled: this.lowmainslockDisabled,
      ticksArray: emptyTicks
    };
  }

  private propDefault<T>(propKey: string, fallback: T): T {
    const prop = this.properties[propKey];
    return (prop && prop.default !== undefined) ? prop.default : fallback;
  }

  private propMin(propKey: string, fallback: number): number {
    return this.properties[propKey]?.minimum ?? fallback;
  }

  private propMax(propKey: string, fallback: number): number {
    return this.properties[propKey]?.maximum ?? fallback;
  }

  // Device reads store the payload under the backend's featureName casing.
  private readStoredFeatureData(): LegacyAny {
    for (const key of ['LowMainsLock', 'lowmainslock', 'LOWMAINSLOCK']) {
      const data = this.appStateService.getFeatureData(key);
      if (data && Object.keys(data).length > 0) {
        return data;
      }
    }
    return null;
  }

  private loadFromSchema(): void {
    if (!this.properties) {
      return;
    }

    this.isEnableFeature = this.isEnableFeatureDefault = this.propDefault('Enabled', false);

    this.lowMainsDurationMin = this.propMin('LowMainsDuration', 1);
    this.lowMainsDurationMax = this.propMax('LowMainsDuration', 600);
    this.lowMainsDuration = this.lowMainsDurationDefault = this.propDefault('LowMainsDuration', 600);

    this.lowmainslockDisabled = !this.isEnableFeature;
  }

  private loadDataFromObject(data: LegacyAny): void {
    // Check for both IsEnabled and Enabled (schema uses Enabled)
    if (data.IsEnabled !== undefined) {
      this.isEnableFeature = data.IsEnabled;
    } else if (data.Enabled !== undefined) {
      this.isEnableFeature = data.Enabled;
    }
    if (data.LowMainsDuration !== undefined) {
      this.lowMainsDuration = this.lowMainsDurationDisplayValue = data.LowMainsDuration;
    }
  }

  private setupInputDebouncing(): void {
    this.lowMainsDurationInputSubject.pipe(
      debounceTime(100),
      takeUntil(this.destroy$)
    ).subscribe(value => {
      this.validateAndUpdateField(value, 'lowMainsDuration');
    });
  }

  // Auto-select "Include in configuration" on user edits, and clear the pending
  // request once the values are back to default so it cannot re-select later.
  private syncIncludeInConfigurationRequest(): void {
    if (!this.defaultColor) {
      this.checkboxState.enableLowMainsLockCheckbox();
      this.isIncludedInConfiguration = true;
    } else if (!this.isIncludedInConfiguration) {
      this.checkboxState.disableLowMainsLockCheckbox();
    }
  }

  // Enable/Disable feature checkbox handler
  enableFeatureChanged(checked: boolean): void {
    this.ignoreParentUpdate = true;
    this.isEnableFeature = checked;
    this.lowmainslockDisabled = !checked;

    this.lowMainsDurationOptions = Object.assign({}, this.lowMainsDurationOptions, { disabled: this.lowmainslockDisabled });

    this.checkDefaults();

    this.syncIncludeInConfigurationRequest();

    this.sendErrorStateToParent();
    this.updateFormControl();
    this.ref.detectChanges();
    this.ignoreParentUpdate = false;
  }

  // Slider change handler
  lowMainsDurationChange(value: number): void {
    this.ignoreParentUpdate = true;
    this.inputError['lowMainsDuration'] = false;
    if (value >= this.lowMainsDurationMin && value <= this.lowMainsDurationMax) {
      this.lowMainsDuration = value;
      this.lowMainsDurationDisplayValue = value;
      this.recalcErrorState();
      this.checkDefaults();

      this.syncIncludeInConfigurationRequest();

      this.sendErrorStateToParent();
      this.updateFormControl();
      this.ref.detectChanges();
    }
    this.ignoreParentUpdate = false;
  }

  // Input field change handler (with debouncing)
  onFieldInput(inputEl: HTMLInputElement, type: string): void {
    const sanitized = this.sanitizeInteger(inputEl);
    if (type === 'lowMainsDuration') {
      this.lowMainsDurationInputSubject.next(sanitized);
    }
  }

  // Pasting bypasses the keypress filter, and the [value] binding does not repaint
  // when the parsed number is unchanged, so correct the DOM value in place.
  private sanitizeInteger(inputEl: HTMLInputElement): string {
    const integerPart = /^-?\d*/.exec(inputEl.value)[0];
    if (integerPart !== inputEl.value || inputEl.validity?.badInput) {
      inputEl.value = integerPart;
    }
    return integerPart;
  }

  // Validate and update field value
  private validateAndUpdateField(value: string, type: string): void {
    // Skip if a reset is in progress (pending debounced values should be discarded)
    if (this.isResetting || type !== 'lowMainsDuration') {
      return;
    }

    this.ignoreParentUpdate = true;
    const numValue = parseInt(value, 10);
    const min = this.lowMainsDurationMin;
    const max = this.lowMainsDurationMax;

    // Always update internal value (even if invalid)
    this.lowMainsDuration = numValue;
    this.lowMainsDurationDisplayValue = numValue;

    // Validate input and set error flags
    if (value === '' || value === '-0' || numValue < min || numValue > max || isNaN(numValue)) {
      this.inputError[type] = true;
    } else {
      this.inputError[type] = false;
    }

    // NaN !== default is always true, so even invalid/empty values trigger auto-check
    this.checkDefaults();
    this.syncIncludeInConfigurationRequest();

    this.recalcErrorState();
    this.sendErrorStateToParent();
    this.updateFormControl();
    this.ref.detectChanges();
    this.ignoreParentUpdate = false;
  }

  // Send error state to parent
  private sendErrorStateToParent(): void {
    if (this.isIncludedInConfiguration) {
      this.inputErrors.setLowMainsLockInputError.next(this.lowmainslockDisabledButton);
    } else {
      this.inputErrors.setLowMainsLockInputError.next(false);
    }
  }

  // Validate a single field and update its error flag
  private validateField(type: string, value: number, min: number, max: number): void {
    // null means NaN was saved to storage (JSON converts NaN to null)
    if (value === null || value === undefined || isNaN(value) || value < min || value > max) {
      this.inputError[type] = true;
      this.lowmainslockDisabledButton = true;
    }
  }

  // Revalidate all fields after loading from storage to restore error states
  private revalidateAllFields(): void {
    this.lowmainslockDisabledButton = false;
    this.inputError = {};

    this.validateField('lowMainsDuration', this.lowMainsDuration, this.lowMainsDurationMin, this.lowMainsDurationMax);

    this.sendErrorStateToParent();
  }

  // Recalculate lowmainslockDisabledButton from inputError flags
  private recalcErrorState(): void {
    this.lowmainslockDisabledButton = false;
    for (const type in this.inputError) {
      if (this.inputError[type]) {
        this.lowmainslockDisabledButton = true;
        break;
      }
    }
  }

  // Check if values match defaults
  checkDefaults(): void {
    this.defaultColor = (
      this.isEnableFeature === this.isEnableFeatureDefault &&
      this.lowMainsDuration === this.lowMainsDurationDefault
    );
    this.featureDefaults.setLowMainsLockDefaultColor(this.defaultColor);
  }

  // Reset to defaults
  resetToDefaults(): void {
    // Prevent any pending debounced input values from overriding the reset
    this.isResetting = true;

    // Preserve checkbox state before resetting
    const wasIncludedInConfiguration = this.isIncludedInConfiguration;

    this.isEnableFeature = this.isEnableFeatureDefault;
    this.lowMainsDuration = this.lowMainsDurationDisplayValue = this.lowMainsDurationDefault;
    this.lowmainslockDisabled = !this.isEnableFeatureDefault;
    this.inputError = {};
    this.lowmainslockDisabledButton = false;
    this.defaultColor = true;

    this.initializeSliderOptions();

    this.updateFormControl();
    this.featureDefaults.setLowMainsLockDefaultColor(this.defaultColor);

    // Clear error state when resetting to defaults
    this.inputErrors.setLowMainsLockInputError.next(false);

    // Restore checkbox state - if it was checked, keep it checked
    if (wasIncludedInConfiguration) {
      this.isIncludedInConfiguration = true;
      this.checkboxState.enableLowMainsLockCheckbox();
    }

    this.ref.detectChanges();

    // Release resetting flag after debounce period to discard any pending values
    setTimeout(() => {
      this.isResetting = false;
    }, 150);
  }

  // Update form control with all values
  private updateFormControl(): void {
    if (!this.formControl) {
      return;
    }
    const value = {
      Enabled: this.isEnableFeature,
      LowMainsDuration: this.lowMainsDuration
    };
    this.formControl.setValue(value);
    this.setConfigurationData(value, 'LowMainsLock');
  }

  private setConfigurationData(value: LegacyAny, keyVal: string): void {
    if (value != null) {
      // Saves to both ConfigurationData and SaveSetData
      const lowmainslockObj = Object.assign({}, value);
      this.appStateService.setFeatureData(keyVal, lowmainslockObj);
    }
  }

  // Safe value for slider one-way binding (prevents slider from receiving null/NaN/out-of-range)
  getSliderSafeValue(value: number, min: number, max: number): number {
    if (value === null || value === undefined || isNaN(value)) {
      return min;
    }
    if (value < min) {
      return min;
    }
    if (value > max) {
      return max;
    }
    return value;
  }

  // Normalize input value on blur (e.g. "007" -> "7", force DOM update)
  onFieldBlur(inputEl: HTMLInputElement, type: string): void {
    const numericValue = parseInt(this.sanitizeInteger(inputEl), 10);
    const cleaned = isNaN(numericValue) ? '' : String(numericValue);
    // Always force DOM update by clearing first, then setting the clean value
    inputEl.value = '';
    inputEl.value = cleaned;
    if (type === 'lowMainsDuration') {
      this.lowMainsDurationDisplayValue = isNaN(numericValue) ? '' : numericValue;
    }
  }

  // Number only validation for input fields
  numberOnly(event: LegacyAny): boolean {
    const charCode = (event.which) ? event.which : event.keyCode;
    return !(charCode > 31 && (charCode < 48 || charCode > 57));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
